import React, { useState } from "react";
import { Button, TextField, Slider, Box, Typography, Switch, FormControlLabel, MenuItem, Select, InputLabel, FormControl, Divider, IconButton, Drawer, useMediaQuery, useTheme, Popover, Popper } from "@mui/material";
import { MusicNote, VolumeUp, AccessAlarm, Star, Settings, Share } from "@mui/icons-material";
import moment from "moment";
import PlaybackMetronome from "./PlaybackMetronome";
import { generateMetronomeTrack } from "../utils/generateMetronomeTrack";
import BPMAnalyzer from "./BPMAnalyzer";
import { FacebookIcon, FacebookShareButton, LinkedinIcon, LinkedinShareButton, RedditIcon, RedditShareButton, TwitterIcon, TwitterShareButton, WhatsappIcon, WhatsappShareButton, XIcon } from "react-share";
import { useTranslation } from 'react-i18next';
import Buymeacoffee from "./BuyMeACoffeeWidget";

const MetronomeApp = () => {
  const { t } = useTranslation();

  const [tempo, setTempo] = useState(120);
  const [duration, setDuration] = useState(60);
  const [accentEnabled, setAccentEnabled] = useState(true);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [timeSignature, setTimeSignature] = useState("4/4");
  const [tempoInput, setTempoInput] = useState(120);
  const [soundType, setSoundType] = useState("click");
  const [tapTimes, setTapTimes] = useState([]); 
  const [openDrawer, setOpenDrawer] = useState(false); // Estado para controlar el Drawer
  const shareUrl = 'https://tempo-track-generator.web.app/'; // URL para compartir (puedes cambiarlo por la URL de la app)
  const shareTitle = t('og_title'); 
  const [anchorEl, setAnchorEl] = useState(null); // Estado para controlar el Popover

  const handleClickShare = (event) => {
    setAnchorEl(event.currentTarget); // Abre el popover cuando se hace clic en el ícono de compartir
  };

  const handleClosePopover = () => {
    setAnchorEl(null); // Cierra el popover
  };

  const open = Boolean(anchorEl); // Determina si el Popover está abierto
  const id = open ? "share-popover" : undefined; 

  const soundUrls = {
    click: "/click.mp3",
    click_accent: "/click_accent.mp3",
    beep: "/beep.mp3",
    beep_accent: "/beep_accent.mp3",
    metronome: "/metronome.mp3",
    metronome_accent: "/metronome_accent.mp3",
    woodBlock: "/woodblock.mp3",
    woodBlock_accent: "/woodblock_accent.mp3",
  };

  // Controladores para los cambios de estado
  const handleTempoChange = (event, newValue) => {
    setTempo(newValue);
    setTempoInput(newValue);
  };

  const handleTempoInputChange = (event) => {
    const value = event.target.value;
    if (!isNaN(value) && value >= 0 && value <= 240) {
      setTempo(value);
      setTempoInput(value);
    }
  };

  const handleDurationChange = (event, newValue) => {
    setDuration(newValue);
  };

  const handleBeatsPerMeasureChange = (event, newValue) => {
    setBeatsPerMeasure(newValue);
  };

  const handleAccentToggle = (event) => {
    setAccentEnabled(event.target.checked);
  };

  const handleTimeSignatureChange = (event) => {
    setTimeSignature(event.target.value);
    const [beats,] = event.target.value.split('/');
    setBeatsPerMeasure(parseInt(beats));
  };

  const handleSoundTypeChange = (soundType) => {
    setSoundType(soundType);
    const audio = new Audio(soundUrls[soundType]);
    audio.play();
  };

  // Función para manejar el tap tempo
  const handleTap = () => {
    const currentTime = Date.now();
    setTapTimes((prevTapTimes) => {
      const newTapTimes = [...prevTapTimes, currentTime];

      if (newTapTimes.length > 1) {
        const intervals = newTapTimes.slice(1).map((time, index) => time - newTapTimes[index]);
        const avgInterval = intervals.reduce((acc, val) => acc + val, 0) / intervals.length;

        const calculatedTempo = Math.round(60000 / avgInterval); 
        setTempo(calculatedTempo);
        setTempoInput(calculatedTempo);
      }

      if (newTapTimes.length > 4) {
        newTapTimes.shift();
      }

      return newTapTimes;
    });
  };

  const handleDownload = () => {
    if (accentEnabled) {
      generateMetronomeTrack(tempo, duration, soundUrls[soundType], soundUrls[`${soundType}_accent`], beatsPerMeasure);
    } else {
      generateMetronomeTrack(tempo, duration, soundUrls[soundType], soundUrls[soundType], beatsPerMeasure);
    }
  };

  const toggleDrawer = () => {
    setOpenDrawer(!openDrawer); // Alternar el estado del Drawer
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Detectar si es un dispositivo móvil
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: isMobile ? 'white' : '#2d2d30',
    }}>
    <Box sx={{
        padding: 3,
        textAlign: "center",
        width: isMobile? '100%' : '80%',
        margin: 'auto',
        border: isMobile ? 'none' : '1px solid #ddd',
        borderRadius: isMobile ? 0 : 2,
        boxShadow: 3,
        backgroundColor: 'white'
      }}>
      <Typography
        variant="h1"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          fontSize: '2.5rem',
          color: '#4A90E2',
          textAlign: 'center',
          lineHeight: '1.2',
          display: 'flex', // Usar flexbox para alinear el ícono con el texto
          justifyContent: 'center', // Centrar los elementos dentro del contenedor
          alignItems: 'center', // Asegurar que el ícono esté centrado verticalmente
        }}
      >
        {!isMobile && ( 
          <MusicNote sx={{ marginRight: 2, fontSize: '1em' }} />
        )}
        {t('title')}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontSize: '1rem',
          color: '#6c757d',
          textAlign: 'center',
          marginTop: 1,
          fontWeight: 'normal',
          marginBottom: 1,
        }}
      >
        {t('subtitle')}
      </Typography>
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: {
          xs: 0, // Para pantallas extra pequeñas (móviles)
          sm: '2em', // Para pantallas pequeñas y superiores (tabletas y más grandes)
          md: '8em', // Para pantallas medianas y superiores
          lg: '10em',
          xl: '12em'
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 10,
      }}>
      <IconButton
        onClick={handleClickShare}
        sx={{
          top: 20,
          left: 20,
          color: '#4A90E2',
        }}
      >
        <Share fontSize="1.5em" />
      </IconButton>

      {/* Popover que contiene los iconos adicionales */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: 100,
          gap: 4,
        }}
      >
          <FacebookShareButton url={shareUrl} quote={shareTitle}>
            <FacebookIcon size={32} round />
          </FacebookShareButton>
          <TwitterShareButton url={shareUrl} title={shareTitle}>
            <XIcon size={32} round />
          </TwitterShareButton>
          <WhatsappShareButton url={shareUrl} title={shareTitle}>
            <WhatsappIcon size={32} round />
          </WhatsappShareButton>
          <LinkedinShareButton url={shareUrl} quote={shareTitle}>
            <LinkedinIcon size={32} round />
          </LinkedinShareButton>
          <RedditShareButton url={shareUrl} quote={shareTitle}>
            <RedditIcon size={32} round />
          </RedditShareButton>
        </Popover>
      </Box>
      <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
          }}>
      {/* Icono de configuración en dispositivos móviles */}
      {isMobile && (
        <IconButton  onClick={toggleDrawer} sx={{ color: '#4A90E2', position: "fixed", top: 20, right: 20 }}>
          <Settings />
        </IconButton>
      )}

      {/* Drawer para mostrar los controles de configuración en dispositivos móviles */}
      {isMobile && (
        <Drawer anchor="right" open={openDrawer} onClose={toggleDrawer}>
          <Box sx={{ width: 250, padding: 3, textAlign: 'center' }}>
          <Typography sx={{marginBottom: '0.6em'}} variant="h5">{t('configuration_title')}</Typography>

          <BPMAnalyzer 
            onBPM={(v) => {
              setTempo(v); 
              setTempoInput(v);
              } 
            } 
            onDuration={setDuration} 
          />
          <Divider />
          
            {/* Tempo Control */}
            <Typography variant="h6">{t('tempo')}</Typography>
            <Slider
              value={tempo}
              onChange={handleTempoChange}
              min={40}
              max={240}
              step={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value} BPM`}
            />
            <TextField
              type="number"
              value={tempoInput}
              onChange={handleTempoInputChange}
              label="BPM manual"
              variant="outlined"
              fullWidth
              sx={{ marginTop: 1 }}
            />

            {/* Tap Tempo Button */}
            <Box sx={{ marginTop: 3, textAlign: 'center', marginBottom: 3 }}>
              <Button variant="outlined" onClick={handleTap}>
                {t('tap_tempo')}
              </Button>
            </Box>
            
            <Divider />

            {/* Duration Control */}
            <Box sx={{ padding: 2, marginBottom: 0 }}>
              <Typography variant="h6">{t('track_duration')}: {moment.utc(duration * 1000).format("mm:ss")}</Typography>
              <Slider
                value={duration}
                onChange={handleDurationChange}
                min={10}
                max={600}
                step={1}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => moment.utc(value * 1000).format("mm:ss")}
              />
            </Box>

            <Divider />

            {/* Time Signature (Beats Per Measure) */}
            <Box sx={{
              padding: 3,
              marginBottom: 2,
            }}>
              {/* Accent Toggle */}
              <Box sx={{ marginBottom: 2, textAlign: 'center' }}>
              <Typography variant="h6">{t('bar')}</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={accentEnabled}
                      onChange={handleAccentToggle}
                      color="primary"
                    />
                  }
                  label={t('accent')}
                />
              </Box>
              <Box sx={{ 
                marginBottom: 2, 
                width: { xs: '100%', sm: '40%' },  
                display: 'flex', 
                justifyContent: 'center',
                flexDirection: 'column',
                margin: 'auto',
                alignItems: 'center' }}>
                <FormControl fullWidth>
                  <InputLabel>{t('signature_label')}</InputLabel>
                  <Select
                    value={timeSignature}
                    onChange={handleTimeSignatureChange}
                    label={t('signature_label')}
                  >
                    <MenuItem value="2/4">2/4</MenuItem>
                    <MenuItem value="3/4">3/4</MenuItem>
                    <MenuItem value="4/4">4/4</MenuItem>
                    <MenuItem value="5/4">5/4</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Button onClick={toggleDrawer}>
              {t('close')}
            </Button>
          </Box>
        </Drawer>
      )}

      {/* Controles visibles siempre (para escritorio) */}
      {!isMobile && (
        <Box sx={{ 
            padding: 3,
            width: { xs: '100%', sm: '50%' },
            borderRadius: 2,
            boxShadow: 3,
          }}>
            <BPMAnalyzer 
            onBPM={(v) => {
              setTempo(v); 
              setTempoInput(v);
              } 
            } 
            onDuration={setDuration} 
          />
          <Divider />

          {/* Tempo Control */}
          <Typography sx={{marginTop: 2}} variant="h6">{t('tempo')}</Typography>
          <Slider
            value={tempo}
            onChange={handleTempoChange}
            min={40}
            max={240}
            step={1}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value} BPM`}
          />
          <TextField
            type="number"
            value={tempoInput}
            onChange={handleTempoInputChange}
            label="BPM manual"
            variant="outlined"
            fullWidth
            sx={{ marginTop: 1 }}
          />

          {/* Tap Tempo Button */}
          <Box sx={{ marginTop: 3, marginBottom: 3 }}>
            <Typography variant="h6">{t('tap_tempo')}</Typography>
            <Button variant="outlined" onClick={handleTap}>
              {t('tap_tempo_pc')}
            </Button>
          </Box>
          
          <Divider />

          {/* Duration Control */}
          <Box sx={{ padding: 3, marginBottom: 2 }}>
            <Typography variant="h6">{t('track_duration')} {moment.utc(duration * 1000).format("mm:ss")}</Typography>
            <Slider
              value={duration}
              onChange={handleDurationChange}
              min={10}
              max={600}
              step={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => moment.utc(value * 1000).format("mm:ss")}
            />
          </Box>
          <Divider />

          {/* Time Signature (Beats Per Measure) */}
          <Box sx={{
            padding: 3,
            marginBottom: 2,
          }}>
            <Typography variant="h6">{t('bar')}</Typography>
            {/* Accent Toggle */}
            <Box sx={{ marginBottom: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={accentEnabled}
                    onChange={handleAccentToggle}
                    color="primary"
                  />
                }
                label={t('accent')}
              />
            </Box>
            <Box sx={{ 
              marginBottom: 2, 
              width: { xs: '100%', sm: '40%' },  
              display: 'flex', 
              justifyContent: 'center',
              flexDirection: 'column',
              margin: 'auto',
              alignItems: 'center' }}>
              <FormControl fullWidth>
                <InputLabel>{t('signature_label')}</InputLabel>
                <Select
                  value={timeSignature}
                  onChange={handleTimeSignatureChange}
                  label={t('signature_label')}
                >
                  <MenuItem value="2/4">2/4</MenuItem>
                  <MenuItem value="3/4">3/4</MenuItem>
                  <MenuItem value="4/4">4/4</MenuItem>
                  <MenuItem value="5/4">5/4</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      )}
         <Box sx={{ 
          width: isMobile ? '100%' : '50%',
          borderRadius: 2,
          boxShadow: 3,
        }}>
        <Box sx={{ marginBottom: 2, padding: 3 }}>
          <Typography variant="h6">{t('metronome_sound')}</Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: 'wrap', marginBottom: 5, marginTop: 2 }}>
            <Button 
              onClick={() => handleSoundTypeChange("click")}
              variant={soundType === "click" ? "contained" : "outlined"}
            >
              <MusicNote /> Slap
            </Button>
            <Button 
              onClick={() => handleSoundTypeChange("beep")}
              variant={soundType === "beep" ? "contained" : "outlined"}
            >
              <AccessAlarm /> Beep
            </Button>
            <Button onClick={() => handleSoundTypeChange("metronome")}
              variant={soundType === "metronome" ? "contained" : "outlined"}
            >
              <Star /> Real
            </Button>
            <Button onClick={() => handleSoundTypeChange("woodBlock")}
              variant={soundType === "woodBlock" ? "contained" : "outlined"}
            >
              <VolumeUp /> Wood Block
            </Button>
          </Box>

      {/* Playback Metronome */}
      <PlaybackMetronome 
        tempo={tempo}
        soundUrl={soundUrls[soundType]}
        accentUrl={soundUrls[`${soundType}_accent`]}
        accent={accentEnabled}
        beatsPerMeasure={beatsPerMeasure}
        isMobile={isMobile}
      />

      {/* Download Button */}
      <Button
        variant="contained"
        onClick={handleDownload}
        sx={{ marginTop: 5 }}
      >
        {t('download')}
      </Button>
      </Box>
      </Box>
      </Box>
      <Typography
        variant="h6"
        sx={{
          fontSize: '1rem',
          color: '#6c757d',
          textAlign: 'center',
          marginTop: 1,
          fontWeight: 'normal',
          marginBottom: 1,
        }}
      >
      <a
          href="https://www.instagram.com/afroda.development"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: '#6c757d' }}
        >
          {t('credits')}
        </a>      
      </Typography>
      <Buymeacoffee />
        </Box>
        </Box>
  );
};

export default MetronomeApp;
